import { TextField } from 'mui-rff';
import React from 'react';
import { z } from 'zod';

import Form from '@dashboard/components/form/Form';
import { alertError, alertSuccess, getApolloErrorMessage } from '@dashboard/contexts/SnackbarManager/store';
import { useCreateSenderEmailAddressMutation, useVerifiedEmailAddressesQuery } from '@dashboard/generated/wp/platform';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import SelectDomainField from './SelectDomainField';

const FORM_SCHEMA = z.object({
  name: z.string().nullish(),
  username: z.string(),
  domainNameVerificationId: z.string(),
});

type Props = {
  initialValues: { domainNameVerificationId: string };
  onClose: () => void;
};
export default function FormDialog({ initialValues, onClose }: Props) {
  const [createSenderAddress, { loading }] = useCreateSenderEmailAddressMutation();
  const { refetch } = useVerifiedEmailAddressesQuery();
  const onSubmit = async (formData: unknown) => {
    try {
      const d = FORM_SCHEMA.parse(formData);
      await createSenderAddress({
        variables: {
          domainNameVerificationId: d.domainNameVerificationId,
          name: d.name,
          username: d.username,
        },
      });
      await refetch();
      alertSuccess('Approved sender added');
      onClose();
    } catch (e) {
      const message = getApolloErrorMessage(e) ?? 'Failed to add an approved sender';
      alertError(message);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Add an approved sender</DialogTitle>
      <Form initialValues={initialValues} onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <Typography paragraph>Add an approved 'From' email address to use in your templates.</Typography>
            <TextField autoFocus label="Name" name="name" placeholder="Jane Smith" />
            <TextField
              label="Email address"
              placeholder="jane"
              name="username"
              InputProps={{
                endAdornment: (
                  <>
                    @<SelectDomainField name="domainNameVerificationId" />
                  </>
                ),
              }}
              margin="normal"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={loading} variant="contained" type="submit">
            Add sender
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}
